import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  timeout: 600000, // 10 min - sending multiple messages at 5s intervals
  withCredentials: true, // send/receive httpOnly auth cookie
});

// Attach JWT bearer token to every request when present in either
// localStorage (remember-me) or sessionStorage (single-session login).
api.interceptors.request.use((config) => {
  try {
    const token = localStorage.getItem("flowup_auth_token")
      || sessionStorage.getItem("flowup_auth_token");
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
  } catch { /* storage not available */ }
  return config;
});

// Normalize FastAPI error responses: when validation fails, `detail` is an array of objects.
// React can't render objects directly, so flatten them into a single string.
// IMPORTANT: structured custom errors (objects with a `code` field — e.g.,
// pilot_quota_exceeded, blocking_company collision) are PRESERVED as-is so
// downstream handlers can show their custom modals.
api.interceptors.response.use(
  (r) => r,
  (error) => {
    const detail = error?.response?.data?.detail;
    if (Array.isArray(detail)) {
      error.response.data.detail = detail
        .map((d) => (typeof d === "object" ? d.msg || JSON.stringify(d) : String(d)))
        .join(", ");
    } else if (detail && typeof detail === "object") {
      // Keep structured custom errors (anything with `code` OR `blocking_company`)
      // intact — the caller's catch block needs them as objects.
      const isStructured =
        typeof detail.code === "string" ||
        typeof detail.blocking_company === "object" ||
        Array.isArray(detail.plans);
      if (!isStructured) {
        error.response.data.detail = detail.msg || JSON.stringify(detail);
      }
    }
    return Promise.reject(error);
  }
);

export async function uploadBatch(file) {
  const formData = new FormData();
  formData.append("file", file);
  const { data } = await api.post("/batches/upload", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

export async function importFromGSheet(url, opts = {}) {
  const { data } = await api.post("/batches/from-gsheet", {
    url,
    company_id: opts.company_id,
    doc_type: opts.doc_type,
  });
  return data;
}

// Saved sheets
export async function getSavedSheets() {
  const { data } = await api.get("/sheets");
  return data;
}

export async function createSavedSheet(payload) {
  const { data } = await api.post("/sheets", payload);
  return data;
}

export async function updateSavedSheet(id, payload) {
  const { data } = await api.put(`/sheets/${id}`, payload);
  return data;
}

export async function deleteSavedSheet(id) {
  const { data } = await api.delete(`/sheets/${id}`);
  return data;
}

export async function runSheetNow(id) {
  const { data } = await api.post(`/sheets/${id}/run-now`);
  return data;
}

export async function deleteBatch(id) {
  const { data } = await api.delete(`/batches/${id}`);
  return data;
}

export async function deleteBatchesBulk(ids) {
  const { data } = await api.post("/batches/delete-bulk", { ids });
  return data;
}

// Tax invoices
export async function getInvoicesByMonth(year, month) {
  const { data } = await api.get(`/invoices/by-month?year=${year}&month=${month}`);
  return data;
}

export async function getPaidInvoices() {
  const { data } = await api.get("/invoices/paid");
  return data;
}

export async function markInvoicePaid(payload) {
  const { data } = await api.post("/invoices/mark-paid", payload);
  return data;
}

export async function updateCustomer(batchId, customerId, payload) {
  const { data } = await api.put(`/batches/${batchId}/customers/${customerId}`, payload);
  return data;
}

export async function syncInvoiceStatuses() {
  const { data } = await api.post("/invoices/sync-statuses");
  return data;
}

export async function resendMessage(payload) {
  const { data } = await api.post("/messages/resend", payload);
  return data;
}

export async function getBatches() {
  const { data } = await api.get("/batches");
  return data;
}

export async function getBatch(id) {
  const { data } = await api.get(`/batches/${id}`);
  return data;
}

export async function sendBatch(batchId, customerIds, messageTemplate, opts = {}) {
  const { data } = await api.post(`/batches/${batchId}/send`, {
    batch_id: batchId,
    customer_ids: customerIds,
    message_template: messageTemplate,
    send_email_too: !!opts.send_email_too,
    interval_seconds: opts.interval_seconds ?? 0,
    scheduled_at: opts.scheduled_at || null,
  });
  return data;
}

export async function getHealth(companyId) {
  const url = companyId ? `/health?company_id=${encodeURIComponent(companyId)}` : "/health";
  const { data } = await api.get(url);
  return data;
}

export async function switchTwilioMode(mode) {
  const { data } = await api.post("/twilio/switch-mode", { mode });
  return data;
}

// Companies (multi-tenant)
export async function listCompanies() {
  const { data } = await api.get("/companies");
  return data;
}

export async function createCompany(payload) {
  const { data } = await api.post("/companies", payload);
  return data;
}

export async function updateCompany(id, payload) {
  const { data } = await api.patch(`/companies/${id}`, payload);
  return data;
}

export async function deleteCompany(id) {
  const { data } = await api.delete(`/companies/${id}`);
  return data;
}

export async function sendCompanyTestEmail(id, toEmail) {
  const { data } = await api.post(`/companies/${id}/send-test-email`, {
    company_id: id,
    to_email: toEmail,
  });
  return data;
}

export async function verifyIntegrations(payload) {
  const { data } = await api.post("/companies/verify-integrations", payload);
  return data;
}
